"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";

/**
 * Footer language switcher.
 *
 * Localized routes exist for the homepage, /guide, /convert, and /faq in
 * de/nl/fr/es (see app/[lang]/). On those surfaces the switcher links to the
 * same page in the chosen language; everywhere else it links to the chosen
 * language's homepage. English is canonical at the bare path.
 */

const LANGS = [
  { code: "en", label: "English" },
  { code: "de", label: "Deutsch" },
  { code: "nl", label: "Nederlands" },
  { code: "fr", label: "Français" },
  { code: "es", label: "Español" },
] as const;

const NON_EN = new Set(["de", "nl", "fr", "es"]);

/** Sub-paths that have a localized equivalent under /[lang]. */
const LOCALIZED_SUBPATHS = new Set(["", "/sounds", "/guide", "/convert", "/faq"]);

function splitPath(pathname: string): { lang: string; rest: string } {
  const segments = pathname.split("/");
  if (NON_EN.has(segments[1])) {
    return { lang: segments[1], rest: "/" + segments.slice(2).join("/") };
  }
  return { lang: "en", rest: pathname };
}

export function LanguageSwitcher() {
  const pathname = usePathname() || "/";
  const { lang: currentLang, rest } = splitPath(pathname);
  const normalizedRest = rest === "/" ? "" : rest.replace(/\/$/, "");
  const mappable = LOCALIZED_SUBPATHS.has(normalizedRest);

  return (
    <nav aria-label="Language" style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 4 }}>
      {LANGS.map((l, i) => {
        const isCurrent = l.code === currentLang;
        const target =
          l.code === "en"
            ? mappable
              ? normalizedRest || "/"
              : "/"
            : `/${l.code}${mappable ? normalizedRest : ""}` || `/${l.code}`;
        return (
          <span key={l.code} style={{ display: "inline-flex", alignItems: "center" }}>
            {i > 0 && (
              <span aria-hidden="true" style={{ color: "var(--tls-fg-faint)", padding: "0 4px", fontSize: "0.75rem" }}>
                ·
              </span>
            )}
            {isCurrent ? (
              <span
                aria-current="true"
                style={{
                  fontSize: "0.75rem",
                  fontFamily: "var(--tls-font-body)",
                  color: "var(--tls-fg-strong)",
                  fontWeight: 600,
                  padding: "0 2px",
                  display: "inline-flex",
                  alignItems: "center",
                  minHeight: "var(--touch-target-min)",
                }}
              >
                {l.label}
              </span>
            ) : (
              <Link prefetch={false}
                href={target}
                hrefLang={l.code}
                lang={l.code}
                style={{
                  fontSize: "0.75rem",
                  fontFamily: "var(--tls-font-body)",
                  fontWeight: 600,
                  color: "var(--tls-fg-subtle)",
                  textDecoration: "underline",
                  textUnderlineOffset: 3,
                  // 44px-tall hit area without changing the visual footprint
                  padding: "0 2px",
                  display: "inline-flex",
                  alignItems: "center",
                  minHeight: "var(--touch-target-min)",
                }}
              >
                {l.label}
              </Link>
            )}
          </span>
        );
      })}
    </nav>
  );
}
